"use client"

import { useState, useEffect } from "react"
import { Star, Heart, Share2, Minus, Plus, ShoppingCart, Truck, RotateCcw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Separator } from "@/components/ui/separator"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { MobileCartCTA } from "@/components/mobile-cart-cta"
import { SafeHtml } from "@/components/ui/safe-html"
import { Skeleton } from "@/components/ui/skeleton"
import { useCart } from "@/contexts/cart-context"
import { useToast } from "@/hooks/use-toast"
import { formatCurrency } from "@/lib/utils"
import { useWishlist } from "@/contexts/wishlist-context"
import { ConversionTracking } from "./conversion-tracking"
import { ImageWithFallback } from "@/components/ui/image-with-fallback"

interface ProductDetailPageProps {
  productSlug: string
}

export function ProductDetailPage({ productSlug }: ProductDetailPageProps) {
  const [product, setProduct] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState(0)
  const [selectedColor, setSelectedColor] = useState<any>(null)
  const [selectedSize, setSelectedSize] = useState<string>("")
  const [quantity, setQuantity] = useState(1)
  const [customText, setCustomText] = useState("")
  const [selectedLevel1, setSelectedLevel1] = useState<any>(null)
  const [selectedLevel2, setSelectedLevel2] = useState<any>(null)
  const [addingToCart, setAddingToCart] = useState(false)

  const { addToCart } = useCart()
  const { toast } = useToast()

  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist()

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(`/api/products/slug/${productSlug}`)
        if (response.ok) {
          const data = await response.json()
          setProduct(data)
          // Set default selections
          if (data.variants && data.variants.length > 0) {
            const level1Variants = data.variants.filter((v: any) => v.level === 1)
            const level2Variants = data.variants.filter((v: any) => v.level === 2)
            
            if (level1Variants.length > 0) {
              setSelectedLevel1(level1Variants[0])
              setSelectedColor(level1Variants[0])
              setSelectedSize(level1Variants[0].name)
              
              // Set level 2 variant if available
              if (level2Variants.length > 0) {
                const child = level2Variants.find((v: any) => v.parentVariantId === level1Variants[0].id)
                if (child) {
                  setSelectedLevel2(child)
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching product:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchProduct()
  }, [productSlug])

  if (loading) {
    return (
      <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          <div className="space-y-4">
            <Skeleton className="aspect-square w-full rounded-lg" />
            <div className="grid grid-cols-4 gap-2">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-md" />
              ))}
            </div>
          </div>
          <div className="space-y-6">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-20 w-full" />
          </div>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Product Not Found</h1>
          <p className="text-muted-foreground">The product you're looking for doesn't exist.</p>
        </div>
      </div>
    )
  }

  const images = product.images ? JSON.parse(product.images) : ["/placeholder.svg"]
  const discount = product.comparePrice 
    ? Math.round(((parseFloat(product.comparePrice) - parseFloat(product.price)) / parseFloat(product.comparePrice)) * 100)
    : 0
  const tags = product.tags ? JSON.parse(product.tags) : []
  const inventory = product.inventory
  const trackQuantity = product.trackQuantity
  const category = product.category

  // Multi-level variant support
  const level1Variants = product.variants?.filter((v: any) => v.level === 1) || []
  const level2Variants = product.variants?.filter((v: any) => v.level === 2) || []

  const handleAddToCart = async () => {
    if (!product) return
    
    setAddingToCart(true)
    try {
      // Determine the price (use variant price if selected, otherwise product price)
      let price = parseFloat(product.price)
      let variantId = undefined
      
      if (selectedLevel2) {
        price = parseFloat(selectedLevel2.price) || price
        variantId = selectedLevel2.id
      } else if (selectedLevel1) {
        price = parseFloat(selectedLevel1.price) || price
        variantId = selectedLevel1.id
      }
      
      await addToCart({
        productId: product.id,
        variantId,
        quantity,
        price,
      })
      
      toast({
        title: "Added to cart!",
        description: `${product.name} has been added to your cart.`,
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add item to cart. Please try again.",
        variant: "destructive",
      })
    } finally {
      setAddingToCart(false)
    }
  }

  return (
    <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-8">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Product Images */}
        <div className="space-y-4">
          <div className="relative aspect-square overflow-hidden rounded-lg border">
            <ImageWithFallback
              src={images[selectedImage] || "/placeholder.svg"}
              alt={product.name}
              fill
              fallbackSrc="/placeholder.svg"
              className="object-cover"
            />
            {product.featured && <Badge className="absolute top-4 left-4">Featured</Badge>}
            {discount > 0 && (
              <Badge variant="destructive" className="absolute top-4 right-4">
                -{discount}%
              </Badge>
            )}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {images.map((image: string, index: number) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`relative aspect-square overflow-hidden rounded-md border-2 ${
                    selectedImage === index ? "border-primary" : "border-border"
                  }`}
                >
                  <ImageWithFallback
                    src={image}
                    alt={`${product.name} ${index + 1}`}
                    fill
                    fallbackSrc="/placeholder.svg"
                    className="object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">{product.name}</h1>
            {/* Category */}
            {category && (
              <div className="mb-2">
                <span className="text-sm text-muted-foreground">Category: </span>
                <a href={`/category/${category.slug}`} className="underline text-primary" target="_blank" rel="noopener noreferrer">
                  {category.name}
                </a>
              </div>
            )}
            {/* Brand */}
            {product.brand && (
              <div className="mb-2">
                <span className="text-sm text-muted-foreground">Brand: </span>
                <a href={`/brand/${product.brand.slug}`} className="text-primary font-medium underline" target="_blank" rel="noopener noreferrer">
                  {product.brand.name}
                </a>
              </div>
            )}
            {/* Tags */}
            {tags.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {tags.map((tag: string) => (
                  <Badge key={tag} variant="secondary">{tag}</Badge>
                ))}
              </div>
            )}
            {/* Inventory */}
            {trackQuantity && (
              <div className="mb-2 text-sm text-muted-foreground">
                {inventory > 0 ? `${inventory} in stock` : "Out of stock"}
              </div>
            )}
            <div className="text-2xl font-bold">
              {formatCurrency(product.price, '₹', { decimals: 0 })}
            </div>
            {product.comparePrice && (
              <div className="line-through text-muted-foreground">
                {formatCurrency(product.comparePrice, '₹', { decimals: 0 })}
              </div>
            )}
          </div>

          <Separator />

          {/* Multi-level Variants Selection */}
          {level1Variants.length > 0 && (
            <div>
              <Label className="text-base font-semibold mb-3 block">
                {level1Variants[0].parameter.charAt(0).toUpperCase() + level1Variants[0].parameter.slice(1)}:
              </Label>
              <div className="flex gap-2 mb-2">
                {level1Variants.map((variant: any) => (
                  <Button
                    key={variant.id}
                    variant={selectedLevel1?.id === variant.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedLevel1(variant)}
                  >
                    {variant.value}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {level2Variants.length > 0 && selectedLevel1 && (
            <div>
              <Label className="text-base font-semibold mb-3 block">
                {level2Variants[0].parameter.charAt(0).toUpperCase() + level2Variants[0].parameter.slice(1)}:
              </Label>
              <div className="flex gap-2 mb-2">
                {level2Variants
                  .filter((v: any) => v.parentVariantId === selectedLevel1.id)
                  .map((variant: any) => (
                    <Button
                      key={variant.id}
                      variant={selectedLevel2?.id === variant.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedLevel2(variant)}
                    >
                      {variant.value}
                    </Button>
                  ))}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div>
            <Label className="text-base font-semibold mb-3 block">Quantity</Label>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" onClick={() => setQuantity(Math.max(1, quantity - 1))}>
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number.parseInt(e.target.value) || 1))}
                className="w-20 text-center"
                min="1"
                max={product.inventory}
              />
              <Button variant="outline" size="icon" onClick={() => setQuantity(quantity + 1)}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {product.inventory} items available
            </p>
          </div>

          {/* Add to Cart */}
          <div className="flex gap-3">
            <Button 
              className="flex-1" 
              size="lg" 
              onClick={handleAddToCart}
              disabled={addingToCart || (trackQuantity && inventory <= 0)}
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              {addingToCart ? "Adding..." : "Add to Cart"}
            </Button>
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => {
                if (isInWishlist(product.id)) {
                  removeFromWishlist(product.id)
                } else {
                  addToWishlist(product.id)
                }
              }}
              className={isInWishlist(product.id) ? "text-red-500" : ""}
            >
              <Heart className={`h-4 w-4 ${isInWishlist(product.id) ? "fill-current" : ""}`} />
            </Button>
            <Button variant="outline" size="icon">
              <Share2 className="h-4 w-4" />
            </Button>
          </div>

        </div>
      </div>

      {/* Product Details Tabs */}
      <div className="mt-16">
        <Tabs defaultValue="description" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="description">Description</TabsTrigger>
            <TabsTrigger value="specifications">Specifications</TabsTrigger>
            <TabsTrigger value="reviews">Reviews (0)</TabsTrigger>
          </TabsList>
          <TabsContent value="description" className="mt-6 text-pretty">
            {product.description ? (
              <SafeHtml content={product.description} />
            ) : (
              <p className="text-muted-foreground">No description available.</p>
            )}
          </TabsContent>
          <TabsContent value="specifications" className="mt-6">
            <div className="grid md:grid-cols-2 gap-4">
              {product.sku && (
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">SKU</span>
                  <span className="text-muted-foreground">{product.sku}</span>
                </div>
              )}
              {product.weight && (
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">Weight</span>
                  <span className="text-muted-foreground">{product.weight} kg</span>
                </div>
              )}
              {product.category && (
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">Category</span>
                  <span className="text-muted-foreground">{product.category.name}</span>
                </div>
              )}
              {product.brand && (
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">Brand</span>
                  <span className="text-muted-foreground">{product.brand.name}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b">
                <span className="font-medium">Status</span>
                <span className="text-muted-foreground">{product.status}</span>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="reviews" className="mt-6">
            <div className="text-center py-8">
              <p className="text-muted-foreground">No reviews yet. Be the first to review this product!</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <MobileCartCTA />
      
      {/* Conversion Tracking */}
      <ConversionTracking 
        pageType="product"
        productId={product?.id}
        productName={product?.name}
        productPrice={product?.price ? parseFloat(product.price) : 0}
      />
    </div>
  )
}
